import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  RotateCcw,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  fetchSetlist,
  fetchSongs,
  togglePlayed,
  resetPlayed,
} from "@/lib/api";
import { formatDuration, formatTotal } from "@/lib/format";

export default function Performance() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [setlist, setSetlist] = useState(null);
  const [songs, setSongs] = useState([]);
  const [focusIndex, setFocusIndex] = useState(0);

  useEffect(() => {
    fetchSetlist(id).then(setSetlist).catch(() => navigate("/setlists"));
    fetchSongs().then(setSongs);
  }, [id]);

  const songMap = useMemo(
    () => Object.fromEntries(songs.map((s) => [s.id, s])),
    [songs]
  );
  const ordered = useMemo(
    () => (setlist?.song_ids || []).map((sid) => songMap[sid]).filter(Boolean),
    [setlist, songMap]
  );

  const playedSet = new Set(setlist?.played_song_ids || []);
  const playedCount = ordered.filter((s) => playedSet.has(s.id)).length;
  const remainingSec = ordered
    .filter((s) => !playedSet.has(s.id))
    .reduce((acc, s) => acc + (s.duration_seconds || 0), 0);
  const totalSec = ordered.reduce((acc, s) => acc + (s.duration_seconds || 0), 0);

  const handleToggle = async (songId) => {
    try {
      const updated = await togglePlayed(id, songId);
      setSetlist(updated);
    } catch {
      toast.error("Erro ao atualizar status");
    }
  };

  const handleReset = async () => {
    try {
      const updated = await resetPlayed(id);
      setSetlist(updated);
      toast.success("Status resetado");
    } catch {
      toast.error("Erro ao resetar");
    }
  };

  if (!setlist) {
    return (
      <div className="min-h-screen bg-[#121212] text-zinc-500 flex items-center justify-center">
        Carregando...
      </div>
    );
  }

  const focusSong = ordered[focusIndex];

  return (
    <div className="min-h-screen bg-[#121212] text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-50 border-b border-[#2D2D2D] bg-[#121212]/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate(`/setlists/${id}`)}
            className="flex items-center text-sm text-zinc-400 hover:text-[#00F0FF] transition-colors"
            data-testid="exit-performance-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Sair
          </button>

          <div className="flex-1 text-center min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#00F0FF]">
              modo performance
            </p>
            <p className="text-sm font-semibold truncate">{setlist.name}</p>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            className="bg-transparent border-[#2D2D2D] text-white hover:bg-[#1A1A1A]"
            data-testid="reset-played-btn"
          >
            <RotateCcw className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Resetar</span>
          </Button>
        </div>

        {/* Progress */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-3 flex items-center gap-4">
          <div className="flex-1 h-1.5 rounded-full bg-[#2D2D2D] overflow-hidden">
            <div
              data-testid="progress-bar"
              className="h-full bg-[#00F0FF] transition-all duration-300"
              style={{
                width: `${ordered.length === 0 ? 0 : (playedCount / ordered.length) * 100}%`,
              }}
            />
          </div>
          <div className="font-mono text-xs text-zinc-400 whitespace-nowrap">
            <span data-testid="played-count" className="text-[#00F0FF]">{playedCount}</span>
            /{ordered.length} faixas · faltam {formatTotal(remainingSec)}
          </div>
        </div>
      </header>

      {/* Focus Card */}
      {focusSong && (
        <section
          className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-12 border-b border-[#2D2D2D]"
          data-testid="focus-card"
        >
          <p className="font-mono text-xs uppercase tracking-[0.24em] text-[#00F0FF] mb-3">
            Tocando agora · #{String(focusIndex + 1).padStart(2, "0")}
          </p>
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05] mb-3">
            {focusSong.title}
          </h1>
          <p className="text-xl sm:text-2xl text-zinc-400 mb-6">{focusSong.artist}</p>

          <div className="flex flex-wrap gap-3 sm:gap-6 font-mono text-base sm:text-lg mb-6">
            <Metric label="TOM" value={focusSong.key || "—"} accent />
            <Metric label="BPM" value={focusSong.bpm || "—"} />
            <Metric label="DURAÇÃO" value={formatDuration(focusSong.duration_seconds)} />
          </div>

          {focusSong.equipment && (
            <div className="rounded-lg border border-[#2D2D2D] bg-[#1A1A1A] p-5 mb-4 max-w-3xl">
              <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#00F0FF] mb-2">
                equipamento
              </p>
              <p className="text-base sm:text-lg leading-relaxed text-zinc-200 whitespace-pre-line">
                {focusSong.equipment}
              </p>
            </div>
          )}

          {focusSong.lyrics && (
            <div className="rounded-lg border border-[#2D2D2D] bg-[#1A1A1A] p-5 max-w-3xl">
              <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#00F0FF] mb-2">
                notas / letra
              </p>
              <p className="text-base sm:text-lg leading-relaxed text-zinc-200 whitespace-pre-line font-mono">
                {focusSong.lyrics}
              </p>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 mt-6">
            <Button
              size="lg"
              onClick={() => handleToggle(focusSong.id)}
              className={`h-14 px-6 font-semibold text-base ${
                playedSet.has(focusSong.id)
                  ? "bg-[#2D2D2D] text-zinc-300 hover:bg-[#3a3a3a]"
                  : "bg-[#39FF14] text-black hover:bg-[#5cff43]"
              }`}
              data-testid="focus-toggle-played-btn"
            >
              <Check className="w-5 h-5 mr-2" />
              {playedSet.has(focusSong.id) ? "Marcada como tocada" : "Marcar como tocada"}
            </Button>
            <div className="flex items-center gap-2 ml-auto">
              <Button
                size="lg"
                variant="outline"
                onClick={() => setFocusIndex((i) => Math.max(0, i - 1))}
                disabled={focusIndex === 0}
                className="h-14 w-14 p-0 bg-transparent border-[#2D2D2D] hover:bg-[#1A1A1A]"
                data-testid="focus-prev-btn"
              >
                <ChevronUp className="w-6 h-6" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => setFocusIndex((i) => Math.min(ordered.length - 1, i + 1))}
                disabled={focusIndex >= ordered.length - 1}
                className="h-14 w-14 p-0 bg-transparent border-[#2D2D2D] hover:bg-[#1A1A1A]"
                data-testid="focus-next-btn"
              >
                <ChevronDown className="w-6 h-6" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Setlist checklist */}
      <section className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-10">
        <h2 className="font-mono text-xs uppercase tracking-[0.24em] text-zinc-500 mb-4">
          setlist completo · {formatTotal(totalSec)}
        </h2>
        {ordered.length === 0 ? (
          <p className="text-zinc-500">Esse setlist está vazio.</p>
        ) : (
          <ul className="space-y-2" data-testid="performance-list">
            {ordered.map((song, idx) => {
              const played = playedSet.has(song.id);
              const isFocus = idx === focusIndex;
              return (
                <li
                  key={song.id}
                  data-testid={`perf-row-${song.id}`}
                  className={`flex items-center gap-3 sm:gap-4 p-4 rounded-lg border transition-all duration-150 cursor-pointer ${
                    isFocus
                      ? "border-[#00F0FF] bg-[#00F0FF]/5"
                      : "border-[#2D2D2D] bg-[#1A1A1A] hover:border-[#00F0FF]/40"
                  } ${played ? "played-row" : ""}`}
                  onClick={() => setFocusIndex(idx)}
                >
                  <div onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={played}
                      onCheckedChange={() => handleToggle(song.id)}
                      data-testid={`played-checkbox-${song.id}`}
                      className="w-7 h-7 border-[#2D2D2D] data-[state=checked]:bg-[#39FF14] data-[state=checked]:border-[#39FF14] data-[state=checked]:text-black"
                    />
                  </div>
                  <div className="font-mono text-sm text-zinc-500 w-8 text-center flex-shrink-0">
                    {String(idx + 1).padStart(2, "0")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xl sm:text-2xl font-semibold leading-tight">
                      {song.title}
                    </p>
                    <p className="text-sm text-zinc-500">{song.artist}</p>
                  </div>
                  <div className="hidden sm:flex flex-col items-end font-mono text-sm flex-shrink-0">
                    <span className="text-[#00F0FF]">{song.key || "—"}</span>
                    <span className="text-zinc-500 text-xs">
                      {song.bpm || "—"} BPM · {formatDuration(song.duration_seconds)}
                    </span>
                  </div>
                  <div className="sm:hidden font-mono text-sm text-[#00F0FF] flex-shrink-0">
                    {formatDuration(song.duration_seconds)}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">
        {label}
      </span>
      <span className={`font-mono ${accent ? "text-[#00F0FF]" : "text-white"}`}>
        {value}
      </span>
    </div>
  );
}
